import React, { useCallback, useMemo, useRef, useState } from "react";
import { IFilterPresetRuntime } from "@models/IFilterPresetsUI";
import { PRESET_SEARCH_THRESHOLD, splitPresetTabs, VISIBLE_PRESET_TABS } from "./filterBarUtils";
import { PresetOverflowMenu } from "./PresetOverflowMenu";

type Props = {
    allLabel: string;
    presets: IFilterPresetRuntime[];
    activePresetId: string | null;
    isModified: boolean;
    /** True when no default preset applies (the "All" tab is the initial view) */
    isAllDefault: boolean;
    translate: (id: string, values?: { [key: string]: string | number }) => string;
    onSelect: (preset: IFilterPresetRuntime | null) => void;
    onAdd: () => void;
    /** Opens the manage sidebar (reorder / rename / delete) */
    onManage: () => void;
    /** Right-click on a tab; `preset` is null for the "All [Screen]" tab */
    onContextMenu: (preset: IFilterPresetRuntime | null, x: number, y: number) => void;
};

/**
 * Preset tab bar: "All [Screen]" first, then team presets, then user presets, then "+" and a manage cog.
 * Only the first VISIBLE_PRESET_TABS presets render inline; the rest sit behind a "More" tab whose menu
 * gains a search box from PRESET_SEARCH_THRESHOLD presets. The active preset is always inline.
 * Right-click a tab for its options (default view, delete). A star marks the tab that opens by default.
 */
export const PresetTabs = (props: Props) => {
    const { activePresetId, isModified, translate, presets } = props;
    const hint = translate("right_click_for_options");
    const defaultTitle = translate("is_default_view");
    const moreRef = useRef<HTMLButtonElement>(null);
    const [moreAnchor, setMoreAnchor] = useState<DOMRect | null>(null);

    const { visible, overflow } = useMemo(() => splitPresetTabs(presets, activePresetId, VISIBLE_PRESET_TABS), [presets, activePresetId]);

    const closeMore = useCallback(() => setMoreAnchor(null), []);

    const star = <i className={"fas fa-star me-1 FilterBar-default-star"} title={defaultTitle} aria-label={defaultTitle} />;

    return (
        <div className={"FilterBar-tabs d-flex align-items-end flex-wrap"} role={"tablist"}>
            <button
                type={"button"}
                role={"tab"}
                aria-selected={activePresetId === null}
                className={"FilterBar-tab" + (activePresetId === null ? " active" : "")}
                title={hint}
                onClick={() => props.onSelect(null)}
                onContextMenu={(e: React.MouseEvent) => {
                    e.preventDefault();
                    props.onContextMenu(null, e.clientX, e.clientY);
                }}>
                {props.isAllDefault ? star : null}
                {props.allLabel}
            </button>

            {visible.map((preset) => {
                const isActive = preset.id === activePresetId;

                return (
                    <button
                        key={preset.id}
                        type={"button"}
                        role={"tab"}
                        aria-selected={isActive}
                        className={"FilterBar-tab" + (isActive ? " active" : "") + (preset.source === "team" ? " team" : "")}
                        title={preset.source === "team" ? translate("team_preset") + (preset.editable ? ` · ${hint}` : "") : hint}
                        onClick={() => props.onSelect(preset)}
                        onContextMenu={(e: React.MouseEvent) => {
                            if (preset.editable) {
                                e.preventDefault();
                                props.onContextMenu(preset, e.clientX, e.clientY);
                            }
                        }}>
                        {preset.source === "team" ? <i className={"fas fa-users me-1"} /> : null}
                        {preset.isDefault ? star : null}
                        <span>{preset.name}</span>
                        {isActive && isModified ? <span className={"FilterBar-dot"} title={translate("modified")} /> : null}
                    </button>
                );
            })}

            {overflow.length > 0 ? (
                <button
                    ref={moreRef}
                    type={"button"}
                    className={"FilterBar-tab FilterBar-tab-more" + (moreAnchor ? " open" : "")}
                    aria-haspopup={"menu"}
                    aria-expanded={!!moreAnchor}
                    data-testid={"filterbar-more"}
                    onClick={() => setMoreAnchor(moreAnchor ? null : moreRef.current?.getBoundingClientRect() || null)}>
                    {translate("more_n", { count: overflow.length })}
                    <i className={"fas fa-caret-down ms-1"} />
                </button>
            ) : null}

            <button
                type={"button"}
                className={"FilterBar-tab FilterBar-tab-add"}
                title={translate("save_as_new_preset")}
                onClick={props.onAdd}>
                <i className={"fas fa-plus"} />
            </button>

            {presets.length > 0 ? (
                <button
                    type={"button"}
                    className={"FilterBar-tab FilterBar-tab-manage"}
                    title={translate("manage_presets")}
                    aria-label={translate("manage_presets")}
                    data-testid={"filterbar-manage"}
                    onClick={props.onManage}>
                    <i className={"fas fa-cog"} />
                </button>
            ) : null}

            {moreAnchor ? (
                <PresetOverflowMenu
                    overflow={overflow}
                    all={presets}
                    activePresetId={activePresetId}
                    searchable={presets.length >= PRESET_SEARCH_THRESHOLD}
                    anchor={moreAnchor}
                    translate={translate}
                    onSelect={(preset) => {
                        closeMore();
                        props.onSelect(preset);
                    }}
                    onContextMenu={(preset, x, y) => {
                        closeMore();
                        props.onContextMenu(preset, x, y);
                    }}
                    onManage={() => {
                        closeMore();
                        props.onManage();
                    }}
                    onClose={closeMore}
                />
            ) : null}
        </div>
    );
};
